import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSettings, useUpdateSettings } from '@/api/settings';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Configuration → Scheduling → Practice Hours. Drives the visible range of
 * the schedule grid (calStartHour, calEndHour). Lands in settings.practice
 * via PATCH /api/settings/practice; server does a shallow merge so the
 * other practice fields (name, NPI, timezone, …) stay intact.
 */

const START_HOURS = Array.from({ length: 24 }, (_, h) => h);
const END_HOURS = Array.from({ length: 24 }, (_, h) => h + 1);

function formatHour(h: number): string {
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    if (h === 24) return '12 AM (next day)';
    const hour12 = h > 12 ? h - 12 : h;
    return `${hour12} ${h < 12 ? 'AM' : 'PM'}`;
}

interface FormState {
    calStartHour: number;
    calEndHour: number;
}

const EMPTY: FormState = { calStartHour: 7, calEndHour: 19 };

export function PracticeHoursPanel() {
    const { data, isLoading, isError } = useSettings();
    const updateM = useUpdateSettings();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [err, setErr] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!data?.practice) return;
        const p = data.practice;
        setForm({
            calStartHour: typeof p.calStartHour === 'number' ? p.calStartHour : 7,
            calEndHour:   typeof p.calEndHour === 'number' ? p.calEndHour : 19,
        });
    }, [data]);

    function set<K extends keyof FormState>(k: K, v: FormState[K]) {
        setSaved(false);
        setErr(null);
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        setSaved(false);
        if (form.calEndHour <= form.calStartHour) {
            setErr('End hour must be after the start hour.');
            return;
        }
        try {
            await updateM.mutateAsync({
                type: 'practice',
                updates: {
                    calStartHour: form.calStartHour,
                    calEndHour: form.calEndHour,
                },
            });
            setSaved(true);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        }
    }

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (isError) {
        return <div className="p-12 text-center text-error">Couldn't load settings.</div>;
    }

    return (
        <div>
            <div className="mb-1">
                <h2 className="font-display text-lg font-bold text-text">Practice Hours</h2>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                The visible range of the schedule grid. Sessions outside this window are
                still allowed — this only controls what hours render by default.
            </p>

            <Card className="p-5 max-w-3xl">
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="Start hour">
                            {(id) => (
                                <select
                                    id={id}
                                    value={form.calStartHour}
                                    onChange={(e) => set('calStartHour', Number(e.target.value))}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    {START_HOURS.map((h) => (
                                        <option key={h} value={h}>{formatHour(h)}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                        <FormField label="End hour">
                            {(id) => (
                                <select
                                    id={id}
                                    value={form.calEndHour}
                                    onChange={(e) => set('calEndHour', Number(e.target.value))}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    {END_HOURS.map((h) => (
                                        <option key={h} value={h}>{formatHour(h)}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                    </div>

                    {err && <p className="text-sm text-error mb-2">{err}</p>}
                    {saved && <p className="text-sm text-success mb-2">Saved.</p>}

                    <div className="flex justify-end">
                        <Button variant="primary" size="sm" type="submit" disabled={updateM.isPending}>
                            {updateM.isPending ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
